import React, { useEffect, useState } from "react";
import { Card, Table, ButtonGroup, Button } from "react-bootstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import { StudentName } from "../utils/studentName";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";
import Swal from "sweetalert2";

export default function IslamicEditAttendance() {
  const { is_teachId, date, period_id } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const STATUS_OPTIONS = [
    { value: "present", label: t.checkAttendance.statusPresent, color: "success" },
    { value: "late",    label: t.checkAttendance.statusLate,    color: "warning" },
    { value: "leave",   label: t.checkAttendance.statusLeave,   color: "info"    },
    { value: "absent",  label: t.checkAttendance.statusAbsent,  color: "danger"  },
  ];

  const [data, setData]   = useState([]);
  const [topic, setTopic] = useState("");
  const [itrId, setItrId] = useState(null);
  const [roomLabel, setRoomLabel]   = useState("—");
  const [subLabel, setSubLabel]     = useState("");

  const displayDate = (() => {
    if (!date) return "";
    const [y, m, d] = date.split('-');
    return `${parseInt(d)}/${parseInt(m)}/${parseInt(y) + 543}`;
  })();

  useEffect(() => {
    if (!is_teachId || !date || !period_id) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/attendanceForEdit/${is_teachId}/${date}/${period_id}`)
      .then(res => {
        const records = res.data;
        if (records.length > 0) {
          const first = records[0];
          setTopic(first.itr_topic || "");
          setItrId(first.is_teachtracking_itr_id);
          setData(records.map(r => ({
            is_study_id:    r.is_study_id,
            st_idnumber:    r.st_idnumber,
            st_name:        r.st_name,
            st_arabicname:  r.st_arabicname,
            st_gen:         r.st_gen,
            is_att_status:  r.is_att_status,
          })));
        }
      })
      .catch(err => {
        Swal.fire({
          icon: 'error',
          title: err.response?.data?.title || t.editAttendance.errorTitle,
          text:  err.response?.data?.message || t.editAttendance.noData,
        });
      })
      .finally(() => setLoading(false));
  }, [is_teachId, date, period_id, setLoading]);

  useEffect(() => {
    if (!is_teachId) return;
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const yearId = localStorage.getItem('yearId') || 0;
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/mySubjects/${user.t_id || 0}/${yearId}`)
      .then(res => {
        const found = res.data.find(s => String(s.is_teachId) === String(is_teachId));
        if (found) {
          setRoomLabel(`${found.ic_class}/${found.ir_no}`);
          setSubLabel(`${found.isubject_isid1} ${found.isubject_name}`);
        }
      })
      .catch(() => {});
  }, [is_teachId]);

  const handleStatusChange = (index, status) => {
    setData(prev => {
      const next = [...prev];
      next[index] = { ...next[index], is_att_status: status };
      return next;
    });
  };

  const handleSave = () => {
    setLoading(true);
    axiosInstance.put(`${process.env.REACT_APP_API}/islamic-teaching/updateAttendance`, {
      is_teachId: Number(is_teachId),
      date,
      period_id: Number(period_id),
      topic,
      itr_id: itrId,
      records: data.map(s => ({ is_study_id: s.is_study_id, is_att_status: s.is_att_status })),
    })
      .then(() => {
        Swal.fire({ icon: 'success', title: t.editAttendance.saveSuccess, timer: 1500, showConfirmButton: false })
          .then(() => navigate('/islamic/my-subjects'));
      })
      .catch(err => {
        Swal.fire({
          icon: 'error',
          title: err.response?.data?.title || t.editAttendance.errorTitle,
          text:  err.response?.data?.message || t.common.error,
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <React.Fragment>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item">
              <Link to="/islamic/my-subjects">{t.editAttendance.breadcrumb}</Link>
            </li>
            <li className="breadcrumb-item active">{t.editAttendance.breadcrumbEdit(roomLabel)}</li>
          </ol>
          <h4 className="main-title mb-0">
            {t.editAttendance.pageTitle} {roomLabel}
            {subLabel && <span className="text-secondary fs-6 ms-2 arabic-text">{subLabel}</span>}
          </h4>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div className="d-flex align-items-center gap-2">
          <label className="mb-0 fw-semibold">{t.editAttendance.labelDate}</label>
          <span className="form-control" style={{ maxWidth: 180, backgroundColor: '#f8f9fa' }}>{displayDate}</span>
        </div>
        <div className="d-flex align-items-center gap-2 flex-grow-1">
          <label className="mb-0 fw-semibold" style={{ whiteSpace: 'nowrap' }}>{t.editAttendance.labelTopic}</label>
          <input
            type="text"
            className="form-control"
            placeholder={t.editAttendance.topicPlaceholder}
            value={topic}
            onChange={e => setTopic(e.target.value)}
          />
        </div>
      </div>

      <Card className="card-one">
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead className="table-secondary">
              <tr>
                <th style={{ width: 50 }} className="text-center">#</th>
                <th style={{ width: 110 }}>{t.editAttendance.colId}</th>
                <th>{t.editAttendance.colName}</th>
                <th className="text-center" style={{ width: 280 }}>{t.editAttendance.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-secondary py-4">{t.editAttendance.noData}</td></tr>
              ) : data.map((s, idx) => (
                <tr key={s.is_study_id}>
                  <td className="text-center align-middle text-secondary">{idx + 1}</td>
                  <td className="align-middle">{s.st_idnumber}</td>
                  <td className="align-middle"><StudentName student={s} lang={lang} /></td>
                  <td className="text-center align-middle">
                    <ButtonGroup size="sm">
                      {STATUS_OPTIONS.map(opt => (
                        <Button
                          key={opt.value}
                          variant={s.is_att_status === opt.value ? opt.color : `outline-${opt.color}`}
                          onClick={() => handleStatusChange(idx, opt.value)}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between">
          <Link to="/islamic/my-subjects" className="btn btn-secondary btn-sm">
            <i className="ri-arrow-left-line me-1"></i>{t.editAttendance.backBtn}
          </Link>
          {data.length > 0 && (
            <Button variant="primary" size="sm" onClick={handleSave}>
              <i className="ri-save-3-fill me-1"></i>{t.editAttendance.saveBtn}
            </Button>
          )}
        </Card.Footer>
      </Card>
    </React.Fragment>
  );
}
